import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { unifiedAuth } from '../../api/authClient'
import { useAuthStore } from 'shared/store/authStore'

const schema = z.object({
  company_name: z.string().min(1, 'auth.register.validation.companyNameRequired'),
  tin: z
    .string()
    .length(13, 'auth.register.validation.jibLength')
    .regex(/^\d+$/, 'auth.register.validation.jibDigits'),
  business_unit: z.string().optional(),
  address: z.string().optional(),
})

type FormData = z.infer<typeof schema>

export interface AddCompanyFormProps {
  onClose?: () => void
  onSuccess?: () => void
  embedded?: boolean
}

export default function AddCompanyForm({ onClose, onSuccess, embedded = true }: AddCompanyFormProps) {
  const { t } = useTranslation('portal')
  const navigate = useNavigate()
  const setMe = useAuthStore((s) => s.setMe)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const translateMsg = (message: string | undefined) => {
    if (!message) return ''
    return t(message) !== message ? t(message) : message
  }

  const onSubmit = async (data: FormData) => {
    setError(null)
    try {
      await unifiedAuth.addCompany({
        company_name: data.company_name,
        tin: data.tin.replace(/\s/g, ''),
        business_unit: data.business_unit || undefined,
        address: data.address || undefined,
      })
      setSuccess(true)
      const meData = await unifiedAuth.me().then((r) => r.data)
      setMe({
        has_company: meData.has_company,
        is_admin: meData.is_admin,
        company_id: meData.company_id ?? null,
        company_name: meData.company_name ?? null,
      })
      onSuccess?.()
      if (onClose) {
        onClose()
        navigate('/company')
      } else {
        setTimeout(() => navigate('/company'), 1500)
      }
    } catch (e: unknown) {
      const detail =
        e &&
        typeof e === 'object' &&
        'response' in e &&
        (e.response as { data?: { detail?: string } })?.data?.detail
      setError(detail ? String(detail) : t('auth.register.errorDefault'))
    }
  }

  const inputClass = embedded
    ? 'w-full rounded-lg bg-white border border-slate-300 px-3 py-2 text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:outline-none'
    : 'w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-slate-200 placeholder-slate-500 focus:border-emerald-500 focus:outline-none'
  const labelClass = embedded
    ? 'block text-sm font-medium text-slate-600 mb-1'
    : 'block text-sm font-medium text-slate-400 mb-1'

  if (success) {
    return (
      <div
        className={embedded ? 'rounded-xl border border-slate-200 bg-slate-50 p-6 text-center' : 'w-full max-w-sm rounded-xl bg-slate-900 border border-slate-800 p-6 shadow-xl text-center'}
        data-testid="add-company-success"
      >
        <h2 className={`text-xl font-semibold mb-2 ${embedded ? 'text-slate-900' : 'text-white'}`}>
          {t('auth.addCompany.successTitle')}
        </h2>
        <p className={`text-sm ${embedded ? 'text-slate-600' : 'text-slate-400'}`}>
          {t('auth.addCompany.successMessage')}
        </p>
        {onClose && (
          <button
            type="button"
            onClick={() => { onClose(); navigate('/company') }}
            className="mt-4 text-blue-600 hover:underline font-medium"
          >
            {t('auth.addCompany.goToDashboard')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className={embedded ? undefined : 'w-full max-w-sm rounded-xl bg-slate-900 border border-slate-800 p-6 shadow-xl'} data-testid="add-company-form">
      {embedded && (
        <p className="text-slate-600 text-sm mb-4">
          {t('auth.addCompany.subtitle')}
        </p>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className={labelClass}>{t('auth.register.companyName')}</label>
          <input
            {...register('company_name')}
            type="text"
            className={inputClass}
            placeholder={t('auth.register.placeholderCompanyName')}
          />
          {errors.company_name && (
            <p className="mt-1 text-sm text-red-500">{translateMsg(errors.company_name.message)}</p>
          )}
        </div>
        <div>
          <label className={labelClass}>{t('auth.register.jib')}</label>
          <input
            {...register('tin')}
            type="text"
            inputMode="numeric"
            maxLength={13}
            className={inputClass}
            placeholder={t('auth.register.placeholderJib')}
          />
          {errors.tin && (
            <p className="mt-1 text-sm text-red-500">{translateMsg(errors.tin.message)}</p>
          )}
        </div>
        <div>
          <label className={labelClass}>{t('auth.register.businessUnit')}</label>
          <input {...register('business_unit')} type="text" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>{t('auth.register.address')}</label>
          <input {...register('address')} type="text" className={inputClass} />
        </div>
        {error && <p className="text-sm text-red-500">{error}</p>}
        <button
          type="submit"
          className={`w-full rounded-lg font-medium py-2 px-4 transition-colors ${
            embedded ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
          }`}
        >
          {t('auth.addCompany.submitButton')}
        </button>
      </form>
    </div>
  )
}
